import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { fetchDashboardList } from '../../../../services/dashboardService';

/**
 * Fetches the list of available dashboards for a given scope and module,
 * determines the initial selected dashboard, and exposes selection state.
 *
 * Selection priority:
 *  1. is_user_default === true
 *  2. is_default === true
 *  3. First item in list
 *
 * @param {Object} params
 * @param {string} params.scopeType - e.g. 'CONTACT'
 * @param {string} params.module    - e.g. 'OVERVIEW'
 */
export const useDashboardList = ({ scopeType, module }) => {
  const [dashboards, setDashboards] = useState([]);
  const [selectedUuid, setSelectedUuid] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const fetchIdRef = useRef(0);
  // Tracks whether the initial default dashboard has already been selected,
  // so we don't re-select on refetch (e.g. after filters change).
  const hasSetInitialRef = useRef(false);

  const fetchList = useCallback(async () => {
    if (!scopeType || !module) return;

    const requestId = ++fetchIdRef.current;
    setLoading(true);
    setError(null);

    try {
      const response = await fetchDashboardList(scopeType, module);
      if (requestId !== fetchIdRef.current) return;

      const list = Array.isArray(response.data) ? response.data : [];
      setDashboards(list);

      if (list.length > 0 && !hasSetInitialRef.current) {
        hasSetInitialRef.current = true;
        const userDefault = list.find((d) => d.is_user_default);
        const systemDefault = list.find((d) => d.is_default);
        const initial = userDefault || systemDefault || list[0];
        setSelectedUuid(initial.dashboard_uuid);
      }
    } catch (err) {
      if (requestId !== fetchIdRef.current) return;
      console.error('Error fetching dashboard list:', err);
      setError(err);
    } finally {
      if (requestId === fetchIdRef.current) {
        setLoading(false);
      }
    }
  }, [scopeType, module]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const selectDashboard = useCallback((uuid) => {
    setSelectedUuid(uuid);
  }, []);

  const selectedDashboard = useMemo(
    () => dashboards.find((d) => d.dashboard_uuid === selectedUuid) || null,
    [dashboards, selectedUuid],
  );

  return {
    dashboards,
    selectedUuid,
    selectedDashboard,
    selectDashboard,
    loading,
    error,
    refetch: fetchList,
  };
};
